export type UserStatus = 'subscribed' | 'unsubscribed' | 'bounced'
export type SaleStatus = 'paid' | 'failed' | 'refunded'

// Vulnerability Enums
export enum Severity {
    INFO = 'INFO',
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH',
    CRITICAL = 'CRITICAL',
}

export enum Status {
    FIXED = 'FIXED',
    NOT_FIXED = 'NOT_FIXED',
    READY_TO_REVALIDATE = 'READY_TO_REVALIDATE',
    OUT_OF_SCOPE = 'OUT_OF_SCOPE'
}

export enum PublishedStatus {
    DRAFT = 'DRAFT',
    UNPUBLISHED = 'UNPUBLISHED',
    PUBLISHED = 'PUBLISHED',
    REJECTED = 'REJECTED'
}

export enum HistoryAction {
    CREATE = 'CREATE',
    STATUS_CHANGE = 'STATUS_CHANGE',
    PUBLISH_STATUS_CHANGE = 'PUBLISH_STATUS_CHANGE',
    SCAN_INITIATED = 'SCAN_INITIATED',
}

// Authentication Types
export interface AuthUser {
    id: string
    firstName: string
    lastName: string
    email: string
    phone: string
    createdAt: string
    updatedAt: string
    accessToken: string
    refreshToken: string
    roles: string[]
}

export interface LoginRequest {
    email: string
    password: string
}

export interface LoginResponse {
    success: boolean
    message: string
    data: {
        user: AuthUser
    }
}

// Role Types
export type UserRole = 'ADMIN' | 'TENANT_ADMIN' | 'TENANT_USER' | 'PENTESTER'
export type TenantRole = 'TENANT_ADMIN' | 'TENANT_USER'

export interface RolePermissions {
    canViewDashboard: boolean
    canManageUsers: boolean
    canManageTenants: boolean
    canManageProjects: boolean
    canViewVulnerabilities: boolean
    canManageVulnerabilities: boolean
    canRunScans: boolean
    canViewReports: boolean
    canManageSettings: boolean
    canViewAssignedAssets: boolean
    canViewAssignedProjects: boolean
}

export interface User {
    id: number
    name: string
    email: string
    avatar?: {
        src: string
        alt: string
    }
    status: UserStatus
    location: string
}

export interface Project {
    id: string
    name: string
    client: string
    lastModified: string
    assetCount: number
    status: 'to_do' | 'in_progress' | 'completed' | 'on_hold' | 'in_review'
    progress: number
    description?: string
    startDate: string
    endDate?: string
}

export type AssetSeverity = 'Critical' | 'High' | 'Medium' | 'Low' | 'Info'

export interface Asset {
    id: string
    name: string
    type: 'Web App' | 'API' | 'Mobile App' | 'Network' | 'Cloud' | 'Other'
    assignedDate: string
    numberOfScans: number
    severity: AssetSeverity
    category?: string
    createdOn?: string
}

export type ScanStatus = 'in_progress' | 'in_review' | 'completed' | 'on_hold'

export interface Scan {
    id: string
    name: string
    vulnerabilities: number
    status: ScanStatus
    startedAt: string
    finishedAt?: string
}

export type VulnerabilityStatus = 'open' | 'in_review' | 'resolved' | 'on_hold'

// Legacy interface for backward compatibility
export interface Vulnerability {
    id: string
    title: string
    name?: string
    scanType: 'Automated' | 'Manual'
    severity: AssetSeverity
    cvss: number
    status: VulnerabilityStatus
    createdOn?: string
    createdAt?: string
    synopsis?: string
    impact?: string
    recommendation?: string
    reference?: string[]
    isSaved?: boolean
}

// New API-based interface
export interface ApiVulnerability {
    _id: string
    tenantId: string
    projectId: string
    assetId: string
    scanId: string
    name: string
    cvss: number
    sla: number
    synopsis: string
    recommendation: string
    impact: string
    createdAt: string
    updatedAt: string
    createdBy?: string
    updatedBy?: string
    deletedAt?: string
    deletedBy?: string
    __v: number
}

export interface Mail {
    id: number
    unread?: boolean
    from: User
    subject: string
    body: string
    date: string
}

export interface Member {
    name: string
    username: string
    role: 'member' | 'owner'
    avatar: {
        src: string
        alt: string
    }
}

export interface Stat {
    title: string
    icon: string
    value: number | string
    variation: number
    formatter?: (value: number) => string
}

export interface Sale {
    id: string
    date: string
    status: SaleStatus
    email: string
    amount: number
}

export interface Notification {
    id: number
    unread?: boolean
    sender: User
    body: string
    date: string
}

export type Period = 'daily' | 'weekly' | 'monthly'

export interface Range {
    start: Date
    end: Date
}

export interface NavigationItem {
    label: string
    icon?: string
    to?: string
    target?: string
    badge?: string
    children?: NavigationItem[]
    type?: 'trigger' | 'checkbox'
    defaultOpen?: boolean
    exact?: boolean
    onSelect?: () => void
}
